// @flow
import { AdSlot, GraphController, Grid, GridItem, MobileGraphController, Query, SeoData, useMedia, } from '@haaretz/htz-components';
import React from 'react';
import { FelaComponent, FelaTheme, } from 'react-fela';

import type { Node, } from 'react';

import AssetStaticGraphs from '../../components/AssetStaticGraphs/AssetStaticGraphs';
import PageRow from '../../components/PageRow/PageRow';
import QuoteAssetsTable from '../../components/QuotePageComponents/QuoteAssetsTable/QuoteAssetsTable';
import QuoteInfoTable from '../../components/QuotePageComponents/QuoteInfoTable/QuoteInfoTable';
import QuoteSummary from '../../components/QuotePageComponents/QuoteSummary/QuoteSummary';
import RelatedAssets from '../../components/QuotePageComponents/RelatedAssets/RelatedAssets';
import RowItem from '../../components/RowItem/RowItem';
import MainLayout from '../../layouts/MainLayout';
import EtfQuery from '../queries/etfData.graphql';
import useAllAdUnits from '../../components/AdManager/AdUnitsProvider';
import useAdUnitPathTemplate, { createAdSlotProps, } from '../../components/AdManager/useAdUnitPathTemplate';

type Props = {
  url: {
    pathname: string,
    query: {
      assetId: string,
      section: string,
    },
    asPath: string,
  },
};

function etf({
  url: {
    query: { section, assetId, },
    asPath,
  },
}: Props): Node {
  const isMobile = useMedia({ query: { from: 's', }, });
  const adUnits = useAllAdUnits();
const adUnitPathTemplate = useAdUnitPathTemplate();

  return (
    <MainLayout section={section}>
      <Query query={EtfQuery} variables={{ assetId, }}>
        {({ loading, error, data, }) => {
          if (error) return null;
          if (loading) return null;
          if (!data.assets?.length) return null;

          const {
            name,
            value,
            changePercentage,
            numeralChange,
            subType,
            assetNumber,
            lastTradeTime,
            relatedAssets,
            mtfEtfPolicy,
            policyChangeDate,
            indexExposure,
            ...staticGraphsData
          } = data.assets[0];
          return (
            <FelaTheme>
              {theme => (
                <FelaComponent
                  style={{
                    extend: [
                      theme.mq(
                        { until: 'l', },
                        {
                          display: 'flex',
                          flexDirection: 'column',
                        }
                      ),
                    ],
                  }}
                >
                  <SeoData
                    title={`${name} - קרנות סל - TheMarker Finance`}
                    description={`${name} - למידע עדכני על קרנות סל היכנסו לאתר TheMarker Finance`}
                    path={asPath}
                  />
                  <PageRow lines={2} miscStyles={{ order: '1', }}>
                    <RowItem
                      title={name}
                      miscStyles={{
                        ...theme.type(5),
                      }}
                    />
                  </PageRow>
                  <PageRow
                    miscStyles={{
                      marginBottom: '2rem',
                      order: '2',
                    }}
                  >
                    <QuoteSummary
                      valueData={[
                        { title: 'שער', value: value.toString(), },
                        { title: '% שינוי', value: changePercentage, },
                        { title: 'שינוי באג׳', value: numeralChange, },
                      ]}
                      date={{ title: 'נכון ל:', value: lastTradeTime, }}
                      assetInfo={[
                        { title: 'סוג נייר:', value: subType, },
                        { title: 'מספר נייר:', value: assetNumber, },
                      ]}
                    />
                  </PageRow>
                  <PageRow
                    miscStyles={{
                      marginBottom: '2rem',
                      order: '3',
                    }}
                  >
                    <RelatedAssets assets={relatedAssets} />
                  </PageRow>
                  <PageRow miscStyles={{ order: '4', }}>
                    {
                      isMobile
                        ? <GraphController assetId={assetId} width={900} />
                        : <MobileGraphController assetId={assetId} />
                    }
                  </PageRow>
                  <PageRow miscStyles={{ order: '5', }}>
                    <Grid
                      gutter={2}
                      miscStyles={{
                        paddingStart: '0rem',
                        paddingEnd: '0rem',
                      }}
                    >
                      <QuoteInfoTable
                        id={assetId}
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 1 / 3, },
                        ]}
                        title="נתוני המסחר"
                        tradingStatus
                        fixed
                        fields={[
                          { name: 'value', display: 'שער אחרון', },
                          { name: 'numeralChange', display: 'שינוי באגורות', },
                          { name: 'changePercentage', display: 'שינוי ב-%', },
                          { name: 'lastTradeTime', display: 'מועד עדכון אחרון', type: 'date', },
                          { name: 'dailyLow', display: 'נמוך יומי', },
                          { name: 'dailyHigh', display: 'גבוה יומי', },
                          { name: 'volume', display: 'מחזור', },
                        ]}
                      />
                      <GridItem
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 2 / 3, },
                        ]}
                      >
                        <AssetStaticGraphs {...staticGraphsData} />
                      </GridItem>
                    </Grid>
                  </PageRow>
                  <PageRow miscStyles={{ order: '7', }}>
                    <RowItem>
                      <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner1'], adUnitPathTemplate)} />
                    </RowItem>
                  </PageRow>
                  <PageRow miscStyles={{ order: '6', }}>
                    <RowItem title="מידע כללי" miscStyles={{ marginBottom: '2rem', }}>
                      <Grid
                        gutter={2}
                        miscStyles={{
                          paddingStart: '0rem',
                          paddingEnd: '0rem',
                        }}
                      >
                        <QuoteInfoTable
                          miscStyles={{
                            height: '100%',
                          }}
                          id={assetId}
                          width={[
                            { until: 'l', value: 1, },
                            { from: 'l', value: 1 / 2, },
                          ]}
                          fields={[
                            { name: 'currencyExposure', display: 'חשיפה (הצמדה) למטבע', },
                            { name: 'etfType', display: 'סוג תעודה', },
                            { name: 'etfIssuer', display: 'מנפיק התעודה', },
                            { name: 'issueDate', display: 'תאריך הנפקה', type: 'date', },
                            { name: 'targetIndex', display: 'שם המדד הנעקב', },
                            { name: 'baseAsset', display: 'נכס בסיס', },
                            { name: 'creationFee', display: 'עמלת פדיון ויצירה', },
                          ]}
                        />
                        <QuoteInfoTable
                          miscStyles={{
                            whiteSpace: 'normal',
                            height: '100%',
                          }}
                          id={assetId}
                          width={[
                            { until: 'l', value: 1, },
                            { from: 'l', value: 1 / 2, },
                          ]}
                          fields={[
                            { name: 'trusteeFee', display: 'דמי נאמן', },
                            { name: 'dividendPolicy', display: 'מדיניות דיבידנד', },
                            { name: 'managementFee', display: 'דמי ניהול', },
                            { name: 'mainCurrency', display: 'מטבע עיקרי', },
                            { name: 'variFee', display: 'דמי ניהול משתנים', },
                            { name: 'variFeeActual', display: 'דמי ניהול משתנים בפועל', },
                          ]}
                        />
                      </Grid>
                    </RowItem>
                  </PageRow>
                  <PageRow>
                    <Grid
                      gutter={2}
                      miscStyles={{
                        paddingStart: '0rem',
                        paddingEnd: '0rem',
                      }}
                    >
                      <GridItem
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 1 / 2, },
                        ]}
                      >
                        <RowItem
                          title="מדיניות התעודה"
                          miscStyles={{ marginBottom: '1rem', }}
                        >
                          <FelaComponent
                            as="p"
                            style={{
                              ...theme.type(-1),
                              color: theme.color('neutral', '-3'),
                              marginBottom: '1rem',
                              marginTop: '1rem',
                              textAlign: 'start',
                            }}
                          >
                            {`תאריך שינוי מדיניות: ${new Date(policyChangeDate).toLocaleString(
                              'it-It',
                              {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              }
                            )}`}
                          </FelaComponent>
                          <FelaComponent as="p">{mtfEtfPolicy}</FelaComponent>
                        </RowItem>
                      </GridItem>
                      <GridItem
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 1 / 2, },
                        ]}
                      >
                        <RowItem title="חשיפת התעודה למדדים">
                          {indexExposure ? (
                            <QuoteAssetsTable
                              assets={indexExposure}
                              fields={[
                                {
                                  value: 'indexName',
                                  display: 'נכס בסיס (שם הנכס)',
                                  miscStyles: { fontWeight: '700', },
                                },
                                { value: 'indexWeight', display: 'אחוז בנכס בסיס', },
                              ]}
                            />
                          ) : null}
                        </RowItem>
                      </GridItem>
                    </Grid>
                  </PageRow>
                </FelaComponent>
              )}
            </FelaTheme>
          );
        }}
      </Query>
    </MainLayout>
  );
}

export default etf;
